import React, { useState, useEffect } from 'react';
import '../view-archer-styles.css';

// material-ui: theme, colors, icons
import { Avatar, Button, Chip, Divider, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormLabel, Grid, MenuItem, IconButton, InputLabel, Radio, RadioGroup, FormControlLabel, Paper, Select, Slide, Stack, Typography } from '@mui/material/';
import { TransitionProps } from '@mui/material/transitions';
import { blue, red } from '@mui/material/colors';
import { Close, Attachment } from '@mui/icons-material/';

//components import
import ArcherFilerDocument from '../dataObjects/ArcherFilerDocument';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import AlertInfo from '../../../dataObjects/AlertInfo';
import ArcherResponse from '../dataObjects/ArcherResponse';
import FlaggedItem from '../dataObjects/FlaggedItem';
import FlaggedItemGroup from '../dataObjects/FlaggedItemGroup';
import ArcherIssue from '../dataObjects/ArcherIssue';

interface UploadResponsesDialogProps {
    flaggedItem?: FlaggedItem,
    flaggedItemGroup?: FlaggedItemGroup,
    archerIssue?: ArcherIssue,
    cpfId: number;
    isUploadResponsesDialogOpen: boolean,
    closeUploadResponsesDialog: () => void
}

interface BaseReportType {
    baseReportTypeId: number;
    baseReportTypeDescription: string;
}

interface BaseReport {
    reportId: number;
    reportingPeriod: string;
    reportTypeId: number;
    reportTypeDescription: string;
    netDepositTotal: string;
    isAmended: boolean;
}

enum ArcherUiResponseType {
    ArcherRefundCheckUpload = 16,
    AuditResponsePdf = 17
}

const ResponsesCategories = [
    {
        categoryId: 1,
        categoryDescription: 'Reports'
    },
    {
        categoryId: 2,
        categoryDescription: 'Filer Documents'
    },
    {
        categoryId: 3,
        categoryDescription: 'Upload New Documents'
    },
]


const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="left" ref={ref} {...props} />;
});

const FilerGatewayUploadResponsesDialog = (props: UploadResponsesDialogProps) => {

    let defaultReportYear = new Date().getFullYear();
    const dropDownYears = Array.from(new Array(5), (val, index) => defaultReportYear - index);

    //initial state setup
    const [categoryId, setCategoryId] = useState(1);
    const [filerDocuments, setFilerDocuments] = useState<ArcherFilerDocument[]>();
    const [pdfUrl, setPdfUrl] = useState('');
    const [selectedDocumentPdfId, setDocumentPdfId] = useState(0);
    const [selectedReportId, setReportId] = useState(0);
    const [reportYear, setReportYear] = useState(defaultReportYear);
    const [reportType, setReportType] = useState(1);
    const [baseReportTypes, setBaseReportTypes] = useState<BaseReportType[]>();
    const [filerReports, setFilerReports] = useState<BaseReport[]>();
    const [uploadFileType, setUploadFileType] = useState(ArcherUiResponseType.AuditResponsePdf);

    const closeUploadResponsesDialog = () => {
        props.closeUploadResponsesDialog();
    }

    useEffect(() => {
        getPotentialResponses();
    }, [categoryId])

    const getPotentialResponses = () => {

        if (categoryId === 1) {
            return getReportsResponses();
        };

        if (categoryId === 2) {
            return getFilerDocumentsResponses();
        }
        
    }

    const updateFileType = (event: any) => {
        let fileTypeId = event.target.value;
        setUploadFileType(fileTypeId);
    }

    const getReportsResponses = () => {

        let url = `/Filer/GetBaseReportTypes?cpfId=${props.cpfId}`;
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: /Filer/GetBaseReportTypes',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let types = result.result;
                    setBaseReportTypes(types);
                    if (types.length > 0) {
                        let reportType = types[0].baseReportTypeId;
                        setReportType(reportType);
                    }
                    getFilerReports(reportType, reportYear);
                }
            })
    }

    const getFilerReports = (type: number, year: number) => {
        var params = [
            `reportFilerCpfId=${props.cpfId}`,
            `baseReportTypeId=${type}`,
            `reportYear=${year}`,
            `pageSize=1000`,
            `currentIndex=1`,
        ].join('&');
        let url = `/Filer/GetReportsAndSummary?${params}`;
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: /Filer/GetReportsAndSummary',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let reports = result.items.filter((report: {isAmended: boolean }) => report.isAmended === false);
                    setFilerReports(reports);
                }
            })
    }

    const getFilerDocumentsResponses = () => {

        let url = `/Filer/GetDocuments?cpfId=${props.cpfId}`;
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetEnclosureTypes',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let data = result.result;
                    setFilerDocuments(data)
                }
            })
    }

    const updateCategory = (id: number) => {
        setCategoryId(id);
        setPdfUrl('');
    }

    const updateReportYear = (event: any) => {
        let year = event.target.value;
        setReportYear(year);
        getFilerReports(reportType, year);
    }

    const updateReportType = (event: any) => {
        let type= event.target.value;
        setReportType(type);
        getFilerReports(type, reportYear);
    }

    const selectReport = (report: BaseReport) => {
        let url = `https://www.ocpf.us/Reports/DisplayReport?menuHidden=true&id=${report.reportId}`;
        setPdfUrl(url);
        setReportId(report.reportId);
        setDocumentPdfId(0);
    }

    const selectDocument = (document: ArcherFilerDocument) => {
        setPdfUrl(document.azureUrl);
        setDocumentPdfId(document.id);
        setReportId(0);
    };

    const addResponse = () => {
        let flaggedItem = props.flaggedItem;
        let flaggedItemGroup = props.flaggedItemGroup;
        let archerIssue = props.archerIssue;
        let issueId = flaggedItem ? flaggedItem.issueId : 0;
        if (flaggedItemGroup && flaggedItemGroup.flaggedItems.length>0) {
            issueId = flaggedItemGroup.flaggedItems[0].issueId;
        }
        let groupUi = flaggedItemGroup ? flaggedItemGroup.groupUi : '';
        let flaggedItemId = flaggedItem ? flaggedItem.flaggedItemId : 0;

        if (archerIssue && issueId===0) {
            issueId = archerIssue.issueId;
        }

        var params = [
            `cpfId=${props.cpfId}`,
            `issueId=${issueId}`,
            `groupUi=${groupUi}`,
            `flaggedItemId=${flaggedItemId}`,
            `pdfId=${selectedDocumentPdfId}`,
            `reportId=${selectedReportId}`,
        ].join('&');
        let url = `/Archer3/AddResponse?${params}`;
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: /Archer3/AddResponse',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    props.closeUploadResponsesDialog();
                }
            })
    }

    const uploadUserDocument = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);

        let flaggedItem = props.flaggedItem;
        let flaggedItemGroup = props.flaggedItemGroup;
        let issueId = flaggedItem ? flaggedItem.issueId : 0;
        let flaggedItemId = flaggedItem ? flaggedItem.flaggedItemId : 0;

        if (flaggedItemGroup && flaggedItemGroup.flaggedItems.length > 0) {
            issueId = flaggedItemGroup.flaggedItems[0].issueId;
        }
        let groupUi = flaggedItemGroup ? flaggedItemGroup.groupUi : '';
        //re-visit
        //let responseType = flaggedItemGroup ? ArcherUiResponseType.ArcherRefundCheckUpload : ArcherUiResponseType.AuditResponsePdf;

        let responseType = uploadFileType;

        formData.append("issueId", issueId.toString());
        formData.append("flaggedItemId", flaggedItemId.toString());
        formData.append("responseType", responseType.toString());
        formData.append("groupUi", groupUi);

        fetch('/archer3/UploadResponse', { method: 'POST', body: formData })
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: UploadResponse',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let info = {
                        type: 'success',
                        title: 'New File Uploaded successfully',
                        errorMessage: 'Please review and process the new file to resolve the issue!',
                        secondaryErrorMessage: ''
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                    props.closeUploadResponsesDialog();
                }
            });
    }

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    return (
        <>
            <Dialog
                open={props.isUploadResponsesDialogOpen}
                scroll={'paper'}
                fullWidth={true}
                maxWidth={'md'}
                TransitionComponent={Transition}
                keepMounted
                onClose={closeUploadResponsesDialog}
                sx={{
                    "& .MuiDialog-container": {
                        justifyContent: "flex-end",
                        alignItems: "flex-end"
                    }
                }}
                PaperProps={{
                    sx: {
                        m: 0,
                        top: 0,
                        right: 0,
                        bottom: 0,
                        minHeight: '100vh'
                    }
                }}
            >
                <DialogTitle>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: blue[500] }}><Attachment /></Avatar>
                        <Typography variant="subtitle1" sx={{ color: blue[800] }}>
                            Select or Upload A Response...
                        </Typography>
                    </Stack>
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={closeUploadResponsesDialog}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <Close />
                </IconButton>
                <DialogContent dividers>
                    <Grid container>
                        <FormControl>
                            <RadioGroup
                                row
                                defaultValue={1}
                            >
                                {
                                    ResponsesCategories.map((category) =>
                                        <FormControlLabel value={category.categoryId} key={category.categoryId} control={<Radio />} label={category.categoryDescription} onClick={() => { updateCategory(category.categoryId) }} />
                                    )
                                }
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                    <Divider light />
                    <Grid container sx={{ marginTop: 2 }}>
                        <Grid item xs={5}>
                            <Paper sx={{ height: 600, p: 1, overflow: 'auto' }}>
                                {
                                    categoryId === 1 &&
                                    <>
                                        <Stack direction="row" spacing={1}>
                                            <FormControl sx={{ m: 1, minWidth: 140 }} size="small">
                                                <InputLabel>Report Year</InputLabel>
                                                <Select
                                                    label="Report Year"
                                                    value={reportYear}
                                                    onChange={updateReportYear}
                                                >
                                                    {
                                                        dropDownYears.map((year) =>
                                                            <MenuItem value={year} key={year} >{year}</MenuItem>
                                                        )
                                                    }
                                                </Select>
                                            </FormControl>
                                            <FormControl sx={{ m: 1, minWidth: 160 }} size="small">
                                                <InputLabel>Report Type</InputLabel>
                                                <Select
                                                    label="Report Type"
                                                    value={reportType}
                                                    onChange={updateReportType}
                                                >
                                                    {
                                                        baseReportTypes && baseReportTypes.length > 0 &&
                                                        baseReportTypes.map((type) =>
                                                            <MenuItem key={type.baseReportTypeId} value={type.baseReportTypeId}>{type.baseReportTypeDescription}</MenuItem>
                                                        )
                                                    }
                                                </Select>
                                            </FormControl>
                                        </Stack>
                                        {
                                            filerReports && filerReports.length >0 && 
                                            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ marginTop: 2 }}>
                                                {
                                                        filerReports.map((report) =>
                                                            <>
                                                                {
                                                                    report.reportTypeId !== 60 &&
                                                                    <Chip key={report.reportId} label={`${report.reportTypeDescription} ${report.reportingPeriod}`} color="primary" variant="outlined" className="is-clickable" onClick={() => selectReport(report)} />
                                                                }
                                                                {
                                                                    report.reportTypeId ===60 &&
                                                                    <Chip key={report.reportId} label={`${report.reportTypeDescription} ${report.reportingPeriod} ${report.netDepositTotal}`} color="primary" variant="outlined" className="is-clickable" onClick={() => selectReport(report)} />
                                                                }
                                                            </>
                                                    )
                                                }
                                            </Stack>
                                        }
                                    </>
                                    
                                }
                                {
                                    categoryId === 2 && filerDocuments && filerDocuments.length>0 &&
                                    <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                                            {
                                                filerDocuments.map((document) =>
                                                    <Chip key={document.id} label={`${document.documentDateDisplay} ${document.documentTypeDescription}`} color="primary" variant="outlined" className="is-clickable" onClick={() => selectDocument(document)} />
                                                )
                                            }
                                    </Stack>
                                }
                                {
                                    categoryId === 3 &&
                                    <form onSubmit={(event: React.FormEvent<HTMLFormElement>) => uploadUserDocument(event)}>
                                            <FormControl>
                                                <FormLabel>Please choose the file type to be uploaded:</FormLabel>
                                                <RadioGroup
                                                    name="uploadFileType"
                                                    value={uploadFileType}
                                                    onChange={updateFileType}
                                                >
                                                    <FormControlLabel value="17" control={<Radio />} label="Audit Response File" />
                                                    <FormControlLabel value="16" control={<Radio />} label="Refund Check Related File" />
                                                </RadioGroup>
                                            </FormControl>
                                            <Stack direction="row" spacing={1} sx={{marginTop:1}}>
                                                <input type="file" name="uploadedFile" />
                                                <Button type="submit" className="is-clickable" variant="text" color="primary" size="small"><Chip variant="outlined" label="Upload" color="primary" size="small" /></Button>
                                            </Stack>
                                     </form>
                                }
                            </Paper>
                        </Grid>
                        <Grid item xs={1}></Grid>
                        <Grid item xs={6}>
                            {
                                (categoryId === 1 || categoryId === 2) && 
                                <Paper>
                                    <div>
                                        <iframe src={pdfUrl} width="100%" height="600px" />
                                    </div>
                                </Paper>
                            }
                            
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeUploadResponsesDialog} variant="contained" color="warning">Cancel</Button>
                    <Button onClick={addResponse} variant="contained" color="success">Add Response</Button>
                </DialogActions>
            </Dialog>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </>
    )

}

export default FilerGatewayUploadResponsesDialog